import { Button } from '@ics-portal/guardian';
import type { ResolvedColumn } from '../../domain';
import type { TableAdapter } from '../../ports';
import { Icon } from '../shared/Icon';
import { useClickOutside } from '../shared/use-click-outside';
import { Chip } from './Chip';
import { ColumnDropdown } from './ColumnDropdown';
import { ValueDropdown } from './ValueDropdown';
import { DateDropdown } from './DateDropdown';
import { useSearch } from './use-search';
import { useSearchActions } from './use-search-actions';
import { useSearchKeys } from './use-search-keys';
import {
    columnKey,
    DATE_OPERATORS,
    filterOptionsByQuery,
    filterableColumns,
    inputTypeFor,
    NUMBER_OPERATORS,
    placeholderFor,
} from './utils';
import './Search.scss';

export interface TableSearchProps<T> {
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
    allColumns?: ResolvedColumn<T>[];
    placeholder?: string;
}

export function TableSearch<T>(props: TableSearchProps<T>): JSX.Element {
    const { placeholder, allColumns } = props;
    const base = useSearch(props);
    const {
        snapshot,
        columns,
        containerRef,
        inputRef,
        inputValue,
        pendingColumn,
        pendingOperator,
        dropdown,
        setDropdown,
    } = base;
    const actions = useSearchActions(base);
    const { selectColumn, selectOperator, selectValue, selectDate } = actions;
    const { onInputKeyDown, onOptionKeyDown } = useSearchKeys(base, actions);

    useClickOutside(containerRef, () => {
        setDropdown(null);
    });

    const lookupColumns = allColumns ?? columns;
    const byKey = new Map<string, ResolvedColumn<T>>();
    for (const c of lookupColumns) {
        byKey.set(columnKey(c), c);
        if (c.id && !byKey.has(c.id)) {
            byKey.set(c.id, c);
        }
    }
    const appliedKeys = new Set(snapshot.filters.map((f) => f.key));
    const columnOptions = filterableColumns(columns, appliedKeys, inputValue);
    const valueOptions =
        pendingColumn?.filter === 'set'
            ? filterOptionsByQuery(
                  pendingColumn.filterOptions ?? [],
                  inputValue,
              )
            : [];
    const operatorOptions =
        pendingColumn?.filter === 'number' ? NUMBER_OPERATORS : DATE_OPERATORS;

    return (
        <div ref={containerRef} className="ics-table__search">
            {}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click delegates focus to the input */}
            <div
                className="ics-table__search-bar"
                onClick={() => {
                    inputRef.current?.focus();
                }}
            >
                <Icon name="search" className="ics-table__search-icon" />
                {snapshot.search ? (
                    <span className="ics-table__search-chip">
                        {snapshot.search}
                        <Button
                            icon={<Icon name="close" />}
                            className="ics-table__search-chip-remove"
                            aria-label="Remove search"
                            onClick={(e) => {
                                e.stopPropagation();
                                base.adapter.setSearch('');
                            }}
                        />
                    </span>
                ) : null}
                {snapshot.filters.map((f) => (
                    <Chip
                        key={f.key}
                        filter={f}
                        column={byKey.get(f.key)}
                        onRemove={base.removeChip}
                    />
                ))}
                {pendingColumn ? (
                    <span className="ics-table__search-chip ics-table__search-chip--pending">
                        {pendingColumn.header}:
                        {pendingOperator ? ` ${pendingOperator.label}` : ''}
                    </span>
                ) : null}
                <input
                    ref={inputRef}
                    type={inputTypeFor(pendingColumn)}
                    role={pendingColumn ? undefined : 'searchbox'}
                    aria-label="Search"
                    autoComplete="off"
                    className="ics-table__search-input"
                    value={inputValue}
                    placeholder={placeholderFor(pendingColumn, placeholder)}
                    onChange={(e) => {
                        base.onInputChange(e.target.value);
                    }}
                    onFocus={base.onInputFocus}
                    onKeyDown={onInputKeyDown}
                />
            </div>

            {dropdown === 'column' && columnOptions.length > 0 ? (
                <ColumnDropdown
                    columns={columnOptions}
                    onSelect={selectColumn}
                    onKeyDown={onOptionKeyDown}
                />
            ) : null}
            {dropdown === 'value' ? (
                <ValueDropdown
                    options={valueOptions}
                    column={pendingColumn ?? undefined}
                    onSelect={selectValue}
                    onKeyDown={onOptionKeyDown}
                />
            ) : null}
            {dropdown === 'operator' ? (
                <ValueDropdown
                    options={operatorOptions}
                    onSelect={selectOperator}
                    onKeyDown={onOptionKeyDown}
                />
            ) : null}
            {dropdown === 'date' ? (
                <DateDropdown onSelect={selectDate} />
            ) : null}
        </div>
    );
}
